import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import { DEFAULT_ASSIGNED_USE_CASE_IDS } from '../../tools/constants/assignableUseCases';
import { ToolUseCaseAssignment } from '../../tools/components/ToolUseCaseAssignment';
import extraStyles from '../../tools/components/AgentExtras.module.css';
import pluginStyles from '../../ai-tool-plugins/components/ToolPluginsPage.module.css';
import {
  CheckIcon,
  CloseIcon,
  LayersIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
  AlertIcon,
} from '../../ai-tool-plugins/components/ToolPluginIcons';
import { createAiSkill, deleteAiSkill, listAiSkills, updateAiSkill } from '../services/skillService';
import type { AiSkillDefinition } from '../types/Skill';
import type { AiPromptUseCaseId } from '../../admin/types/AiPromptConfig';

type DrawerMode = 'create' | 'edit' | null;
type StatusFilter = 'all' | 'published' | 'draft';
type FormState = {
  skillId: string;
  name: string;
  description: string;
  body: string;
  isPublished: boolean;
  assignedUseCaseIds: AiPromptUseCaseId[];
};

const emptyForm = (): FormState => ({
  skillId: '',
  name: '',
  description: '',
  body: '',
  isPublished: false,
  assignedUseCaseIds: [...DEFAULT_ASSIGNED_USE_CASE_IDS],
});

export const SkillsPage: React.FC = () => {
  const { t } = useTranslation('agentSkills');
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const canManage = hasPermission([PermissionCodes.aiConfigManage]);
  const [items, setItems] = useState<AiSkillDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [drawerMode, setDrawerMode] = useState<DrawerMode>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formIssue, setFormIssue] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listAiSkills());
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const visibleItems = useMemo(() => {
    const search = query.trim().toLowerCase();
    return items.filter((item) => {
      if (statusFilter === 'published' && !item.isPublished) return false;
      if (statusFilter === 'draft' && item.isPublished) return false;
      if (!search) return true;
      return item.name.toLowerCase().includes(search)
        || item.description.toLowerCase().includes(search)
        || item.skillId.toLowerCase().includes(search);
    });
  }, [items, query, statusFilter]);

  const openCreate = () => {
    setForm(emptyForm());
    setFormIssue(null);
    setDrawerMode('create');
  };

  const openEdit = (item: AiSkillDefinition) => {
    setForm({
      skillId: item.skillId,
      name: item.name,
      description: item.description,
      body: item.body,
      isPublished: item.isPublished,
      assignedUseCaseIds: [...item.assignedUseCaseIds],
    });
    setFormIssue(null);
    setDrawerMode('edit');
  };

  const validate = (): string | null => {
    if (!form.name.trim()) return 'nameRequired';
    if (!form.description.trim()) return 'descriptionRequired';
    if (!form.body.trim()) return 'bodyRequired';
    return null;
  };

  const handleSave = async () => {
    if (!canManage) return;
    const issue = validate();
    if (issue) {
      setFormIssue(issue);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        body: form.body.trim(),
        isPublished: form.isPublished,
        assignedUseCaseIds: form.assignedUseCaseIds,
      };
      if (drawerMode === 'edit') {
        await updateAiSkill(form.skillId, payload);
        showToast(t('updated'), 'success');
      } else {
        await createAiSkill({ ...payload, skillId: form.skillId.trim() || undefined });
        showToast(t('created'), 'success');
      }
      setDrawerMode(null);
      await loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: AiSkillDefinition) => {
    if (!canManage) return;
    if (!window.confirm(t('confirmDelete', { name: item.name }))) return;
    try {
      await deleteAiSkill(item.skillId);
      showToast(t('deleted'), 'success');
      await loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToDelete'), 'error');
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className={`${pluginStyles.page} ${extraStyles.page}`}>
      <div className={pluginStyles.pageBar}>
        <div className={pluginStyles.statStrip}>
          <span className={pluginStyles.stat}>
            <span className={pluginStyles.statValue}>{items.length}</span>
            {t('statItems')}
          </span>
          <span className={pluginStyles.stat}>
            <span className={pluginStyles.statValue}>{items.filter((item) => item.isPublished).length}</span>
            {t('statPublished')}
          </span>
        </div>
        {canManage ? (
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            <PlusIcon />
            <span>{t('create')}</span>
          </button>
        ) : null}
      </div>

      {items.length === 0 ? (
        <div className={pluginStyles.empty}>
          <span className={pluginStyles.emptyIcon}><LayersIcon size={22} /></span>
          <span className={pluginStyles.emptyTitle}>{t('emptyTitle')}</span>
          <p className={pluginStyles.emptyText}>{t('emptyText')}</p>
          {canManage ? (
            <button type="button" className="btn btn-primary" onClick={openCreate}>
              <PlusIcon />
              <span>{t('create')}</span>
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className={pluginStyles.listToolbar}>
            <div className={`${pluginStyles.searchField} ${pluginStyles.listToolbarSearch}`}>
              <SearchIcon className={pluginStyles.searchFieldIcon} size={15} />
              <input
                className={pluginStyles.searchInput}
                value={query}
                placeholder={t('search')}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            <div className={pluginStyles.segmented}>
              {(['all', 'published', 'draft'] as StatusFilter[]).map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`${pluginStyles.segment} ${statusFilter === id ? pluginStyles.segmentActive : ''}`}
                  onClick={() => setStatusFilter(id)}
                >
                  {t(id === 'all' ? 'filterAll' : id === 'published' ? 'filterPublished' : 'filterDraft')}
                </button>
              ))}
            </div>
          </div>

          {visibleItems.length === 0 ? (
            <p className={pluginStyles.emptyText}>{t('noSearchResults')}</p>
          ) : (
            <div className={pluginStyles.pluginGrid}>
              {visibleItems.map((item) => (
                <article
                  key={item.skillId}
                  className={`${pluginStyles.pluginCard} ${item.isPublished ? pluginStyles.pluginCardPublished : ''}`}
                >
                  <div className={pluginStyles.pluginCardTop}>
                    <span className={pluginStyles.pluginName}>{item.name}</span>
                    <span className={`${pluginStyles.badge} ${item.isPublished ? pluginStyles.badgeSuccess : pluginStyles.badgeWarn}`}>
                      {item.isPublished ? <CheckIcon size={12} /> : null}
                      {item.isPublished ? t('published') : t('draft')}
                    </span>
                  </div>
                  <p className={pluginStyles.pluginText}>{item.description}</p>
                  <div className={pluginStyles.pluginCardActions}>
                    <button type="button" className={pluginStyles.iconButton} onClick={() => openEdit(item)}>
                      <PencilIcon size={15} />
                    </button>
                    {canManage ? (
                      <button
                        type="button"
                        className={`${pluginStyles.iconButton} ${pluginStyles.iconButtonDanger}`}
                        onClick={() => void handleDelete(item)}
                      >
                        <TrashIcon size={15} />
                      </button>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}

      {drawerMode ? (
        <div className={pluginStyles.drawerOverlay} onClick={() => !saving && setDrawerMode(null)}>
          <div className={pluginStyles.drawer} role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className={pluginStyles.drawerHead}>
              <div>
                <h3 className={pluginStyles.drawerTitle}>{drawerMode === 'edit' ? t('edit') : t('create')}</h3>
                <p className={pluginStyles.drawerSubtitle}>{t('formHelp')}</p>
              </div>
              <button type="button" className={pluginStyles.iconButton} onClick={() => setDrawerMode(null)}>
                <CloseIcon size={15} />
              </button>
            </div>
            <div className={pluginStyles.drawerBody}>
              {formIssue ? (
                <div className={pluginStyles.formError}>
                  <AlertIcon size={15} />
                  <span>{t(`issue_${formIssue}`)}</span>
                </div>
              ) : null}
              <label className={pluginStyles.field}>
                <span className={pluginStyles.fieldLabel}>{t('name')}</span>
                <input
                  className="text-input"
                  value={form.name}
                  placeholder={t('namePlaceholder')}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                />
              </label>
              <label className={pluginStyles.field}>
                <span className={pluginStyles.fieldLabel}>{t('description')}</span>
                <input
                  className="text-input"
                  value={form.description}
                  placeholder={t('descriptionPlaceholder')}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                />
              </label>
              <label className={pluginStyles.field}>
                <span className={pluginStyles.fieldLabel}>{t('body')}</span>
                <textarea
                  className={`text-input ${extraStyles.textarea}`}
                  value={form.body}
                  placeholder={t('bodyPlaceholder')}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, body: event.target.value }))}
                />
              </label>
              <label className={pluginStyles.field}>
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, isPublished: event.target.checked }))}
                />
                <span>
                  <strong>{t('publish')}</strong>
                  <span className={pluginStyles.fieldHint}>{t('publishHelp')}</span>
                </span>
              </label>
              <ToolUseCaseAssignment
                selectedUseCaseIds={form.assignedUseCaseIds}
                isPublished={form.isPublished}
                disabled={!canManage || saving}
                onChange={(assignedUseCaseIds) => setForm((current) => ({ ...current, assignedUseCaseIds }))}
              />
            </div>
            {canManage ? (
              <div className={pluginStyles.drawerFoot}>
                <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void handleSave()}>
                  {saving ? t('saving') : t('save')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SkillsPage;
